import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutGrid, List, SlidersHorizontal, Sparkles } from 'lucide-react';
import { Project, portfolioProjects } from '../data/portfolioData';
import { ProjectCard } from './ProjectCard';

interface ProjectShowcaseProps {
  onSelectProject: (project: Project) => void;
}

export const ProjectShowcase: React.FC<ProjectShowcaseProps> = ({ onSelectProject }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const categories = ['All', 'Residential', 'Commercial', 'Cultural', 'Adaptive Reuse'];

  const filteredProjects = selectedCategory === 'All'
    ? portfolioProjects
    : portfolioProjects.filter((p) => p.category === selectedCategory);

  return (
    <section id="works" className="py-24 px-6 md:px-12 max-w-7xl mx-auto relative z-10 scroll-mt-24">
      {/* Section Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-10 border-b border-white/10">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-mono text-arch-accent tracking-widest uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-arch-accent" />
            <span>01 // Selected Portfolio Portfolio</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-white tracking-tight">
            Built Works & <span className="italic font-normal text-arch-limestone">Typologies</span>
          </h2>
          <p className="text-xs sm:text-sm text-neutral-400 font-light max-w-lg">
            A curated cross-section of private residences, cultural institutions, and adaptive reuse experiments exploring materiality, climate, and structural discipline.
          </p>
        </div>

        {/* View Switcher & Counter */}
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-arch-muted bg-white/5 px-3 py-1.5 rounded border border-white/10">
            <span>Showing:</span>
            <span className="text-arch-accent font-semibold">{filteredProjects.length} Works</span>
          </div>

          <div className="flex items-center bg-white/5 p-1 rounded border border-white/10">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded text-xs transition-colors ${
                viewMode === 'grid' ? 'bg-arch-accent text-[#0A0B0D]' : 'text-neutral-400 hover:text-white'
              }`}
              aria-label="Grid View"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded text-xs transition-colors ${
                viewMode === 'list' ? 'bg-arch-accent text-[#0A0B0D]' : 'text-neutral-400 hover:text-white'
              }`}
              aria-label="List View"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Category Filter Pills */}
      <div className="flex items-center gap-2 overflow-x-auto py-6 no-scrollbar">
        <SlidersHorizontal className="w-3.5 h-3.5 text-neutral-500 mr-2 flex-shrink-0" />
        {categories.map((cat) => {
          const isActive = selectedCategory === cat;
          return (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`relative px-4 py-2 text-xs font-mono uppercase tracking-wider rounded-sm transition-all whitespace-nowrap ${
                isActive
                  ? 'text-[#0A0B0D] font-semibold'
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {isActive && (
                <motion.div
                  layoutId="activeFilter"
                  className="absolute inset-0 bg-arch-accent rounded-sm z-0"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
              <span className="relative z-10">{cat}</span>
            </button>
          );
        })}
      </div>

      {/* Projects Grid / List View */}
      {viewMode === 'grid' ? (
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-4"
        >
          <AnimatePresence>
            {filteredProjects.map((project, idx) => (
              <ProjectCard
                key={project.id}
                project={project}
                onOpen={onSelectProject}
                index={idx}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      ) : (
        /* Editorial List / Monograph Row View */
        <motion.div layout className="divide-y divide-white/10 pt-4">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              onClick={() => onSelectProject(project)}
              className="group py-6 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer hover:bg-white/[0.02] px-4 transition-colors rounded-sm"
            >
              <div className="flex items-start md:items-center gap-6">
                <span className="font-mono text-xs text-arch-accent tracking-widest pt-1 md:pt-0">
                  {project.id}
                </span>
                <div>
                  <h3 className="text-xl font-serif text-white group-hover:text-arch-accent transition-colors font-medium">
                    {project.title}
                  </h3>
                  <div className="text-xs text-neutral-400 font-light flex items-center gap-3 pt-1">
                    <span>{project.location}</span>
                    <span>•</span>
                    <span className="text-arch-muted">{project.specs.typology}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between md:justify-end gap-8 text-xs font-mono text-neutral-400">
                <span>{project.area}</span>
                <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-[10px] text-arch-muted">
                  {project.category}
                </span>
                <span className="text-neutral-500">{project.year}</span>
                <span className="text-arch-accent group-hover:translate-x-1 transition-transform">
                  →
                </span>
              </div>
            </div>
          ))}
        </motion.div>
      )}

      {/* Dynamic Placeholder Help Note for When Brother Gives Works */}
      <div className="mt-16 p-6 rounded-sm bg-white/[0.02] border border-dashed border-white/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-arch-accent/10 border border-arch-accent/30 flex items-center justify-center text-arch-accent flex-shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <div className="text-xs font-mono text-white font-medium">Modular Project Architecture</div>
            <div className="text-xs text-neutral-400 font-light">
              Add or swap high-res photos, drone videos, and CAD drawings inside <code className="text-arch-accent font-mono">src/data/portfolioData.ts</code>.
            </div>
          </div>
        </div>
        <a
          href="#inquire"
          className="text-xs font-mono text-arch-accent hover:underline flex-shrink-0"
        >
          Discuss a Custom Commission →
        </a>
      </div>
    </section>
  );
};
